import { useMemo, useRef, useState } from "react";
import styles from "./slider.module.scss";

interface SliderOptions {
  name: string;
  values: number[];
  min: number;
  max: number;
  staticValues?: number[] | undefined;
  staticLabels?: string[] | undefined;
  stepSize?: number | undefined;
  steps?: number[] | undefined;
  tickSize?: number | undefined;
  ticks?: number[] | undefined;
}

type Props = {
  options: SliderOptions;
  onChange(values: number[]): void;
  onDrag?(values: number[]): void;
};

const sortNumList = (values: number[]) => [...values].sort((a, b) => a - b);

const getPercentageForValue = (value: number, min: number, max: number) =>
  Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));

const getValueForClientX = (
  clientX: number,
  trackRect: { left: number; width: number },
  min: number,
  max: number,
) => {
  const percentage = (clientX - trackRect.left) / trackRect.width;
  return min + (max - min) * percentage;
};

const roundToStep = (
  value: number,
  min: number,
  max: number,
  stepSize?: number,
  steps?: number[],
) => {
  let left = min;
  let right = max;

  if (steps) {
    steps.forEach((step) => {
      if (step <= value && step > left) left = step;
      if (step >= value && step < right) right = step;
    });
  } else {
    const size = stepSize || 1;
    while (left < value && left + size < value) left += size;
    right = Math.min(left + size, max);
  }

  return value - left < right - value ? left : right;
};

const getNextStepValue = (
  value: number,
  direction: number,
  min: number,
  max: number,
  stepSize?: number,
  steps?: number[],
) => {
  if (steps) {
    const currIndex = steps.indexOf(value);
    const nextIndex = currIndex + direction;
    return nextIndex >= 0 && nextIndex < steps.length ? steps[nextIndex] : value;
  }

  const nextValue = value + (stepSize || 1) * direction;
  return nextValue >= min && nextValue <= max ? nextValue : value;
};

const getTicks = (options: SliderOptions) => {
  if (options.ticks) return options.ticks;
  if (options.steps) return options.steps;

  const tickSize = options.tickSize || 10;
  const ticks = [options.min];
  while (ticks[ticks.length - 1] < options.max - tickSize) {
    ticks.push(ticks[ticks.length - 1] + tickSize);
  }
  ticks.push(options.max);
  return ticks;
};

export const Slider = ({ options, onChange, onDrag }: Props) => {
  const { min, max, stepSize, steps } = options;
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeHandleIndex, setActiveHandleIndex] = useState<number | null>(null);
  const [tempValues, setTempValues] = useState<number[] | undefined>(undefined);

  const displayValues = tempValues || options.values;
  const ticks = useMemo(() => getTicks(options), [options]);

  const handlePointerDown = (index: number) => (e: React.PointerEvent) => {
    e.preventDefault();
    setActiveHandleIndex(index);

    let draggedValues = options.values;

    const handleMove = (moveEvent: PointerEvent) => {
      const trackRect = trackRef.current?.getBoundingClientRect();
      if (!trackRect) return;

      const rawValue = getValueForClientX(moveEvent.clientX, trackRect, min, max);
      const newValue = roundToStep(rawValue, min, max, stepSize, steps);
      const newValues = [
        ...draggedValues.slice(0, index),
        newValue,
        ...draggedValues.slice(index + 1),
      ];
      draggedValues = newValues;

      if (onDrag) {
        onDrag(newValues);
      } else {
        setTempValues(newValues);
      }
    };

    const handleUp = () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerup", handleUp);

      const sortedValues = sortNumList(draggedValues);
      onChange(sortedValues);
      onDrag?.(sortedValues);

      setActiveHandleIndex(null);
      setTempValues(undefined);
    };

    document.addEventListener("pointermove", handleMove);
    document.addEventListener("pointerup", handleUp);
  };

  const handleKeyDown = (index: number) => (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;

    e.preventDefault();
    setActiveHandleIndex(index);
    const direction = e.key === "ArrowLeft" ? -1 : 1;
    const newValue = getNextStepValue(options.values[index], direction, min, max, stepSize, steps);
    const newValues = [
      ...options.values.slice(0, index),
      newValue,
      ...options.values.slice(index + 1),
    ];
    onChange(sortNumList(newValues));
  };

  return (
    <div key={options.name}>
      <div className={styles.track} ref={trackRef}>
        <div className={styles.fill} />

        {ticks.map((value, i) => (
          <div
            key={i}
            className={styles.tick}
            style={{ left: `${getPercentageForValue(value, min, max)}%` }}
          >
            <div className={styles.tickLabel}>{value}</div>
          </div>
        ))}

        {options.staticValues?.map((value, index) => (
          <button
            type="button"
            key={`static-${index}`}
            className={styles.handleButton}
            style={{ left: `${getPercentageForValue(value, min, max)}%` }}
            tabIndex={-1}
            aria-hidden="true"
          >
            <div className={`${styles.handle} ${styles.disabled}`}>
              {options.staticLabels?.[index] ?? value}
            </div>
          </button>
        ))}

        {displayValues.map((value, index) => (
          <button
            type="button"
            key={index}
            className={styles.handleButton}
            style={{ left: `${getPercentageForValue(value, min, max)}%` }}
            role="slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={value}
            tabIndex={0}
            onPointerDown={handlePointerDown(index)}
            onKeyDown={handleKeyDown(index)}
          >
            <div className={`${styles.handle} ${activeHandleIndex === index ? styles.active : ""}`}>
              {value}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
